import { PaletteConfig } from "../models/palette.model";

/** Palette ID for the built-in default palette. */
export const BUILT_IN_PALETTE_ID = "pal_jepto";

/**
 * Predefined palettes shipped with the app (`BUILT_IN_PALETTES`). The host may merge
 * additional palettes from the API; duplicates of these ids are dropped on merge.
 */
export const BUILT_IN_PALETTES: PaletteConfig[] = [
  {
    paletteId: BUILT_IN_PALETTE_ID,
    label: "Jepto",
    dark: [
      "#AB7AFF",
      "#90CBFF",
      "#D583E4",
      "#6EEDF9",
      "#B79BEA",
      "#9EABFF",
      "#CBB5FF",
      "#8F66FF",
      "#FF85B3",
      "#7DDBFF",
    ],
    light: [
      "#651FFF",
      "#42A5F5",
      "#AB47BC",
      "#26C6DA",
      "#7E57C2",
      "#5C6BC0",
      "#9575CD",
      "#4527A0",
      "#EC407A",
      "#2196F3",
    ],
  },
  {
    paletteId: "pal_inferno",
    label: "Inferno",
    light: ["#FFD700", "#FFA500", "#FF5500", "#FF0000", "#AA0000", "#500000"],
    dark: ["#FFF959", "#FFD059", "#FF8C38", "#FF5252", "#FF2D2D", "#FF0000"],
  },
  {
    paletteId: "pal_sequential",
    label: "Sequential",
    light: [
      "#0D47A1",
      "#1565C0",
      "#1976D2",
      "#1E88E5",
      "#2196F3",
      "#42A5F5",
      "#64B5F6",
      "#90CAF9",
    ],
    dark: [
      "#2D87FF",
      "#4698FF",
      "#59A9FF",
      "#6BBDFF",
      "#7DCBFF",
      "#90DAFF",
      "#A8E5FF",
      "#C4F0FF",
    ],
  },
  {
    paletteId: "pal_ocean",
    label: "Ocean",
    light: [
      "#1A237E",
      "#303F9F",
      "#3949AB",
      "#7E57C2",
      "#5E35B1",
      "#512DA8",
      "#673AB7",
    ],
    dark: [
      "#5970FF",
      "#768AFF",
      "#8A9DFF",
      "#BE7AFF",
      "#B966FF",
      "#A44DFF",
      "#AC83FF",
    ],
  },
  {
    paletteId: "pal_vibrant",
    label: "Vibrant",
    light: [
      "#2196F3",
      "#4CAF50",
      "#E91E63",
      "#FFC107",
      "#673AB7",
      "#FF9800",
      "#009688",
      "#3F51B5",
    ],
    dark: [
      "#64B5FF",
      "#66E970",
      "#FF5A8F",
      "#FFDF4D",
      "#9F74FF",
      "#FFBB52",
      "#4DFFF0",
      "#7A8CFF",
    ],
  },
];

/** Built-in palette ids — kept in sync with {@link BUILT_IN_PALETTES}. */
export const BUILT_IN_PALETTE_IDS: readonly string[] = BUILT_IN_PALETTES.map(
  (p) => p.paletteId
);

/**
 * Returns true if `paletteId` is one of the built-in palettes in {@link BUILT_IN_PALETTES}.
 */
export function isBuiltInPaletteId(paletteId: string): boolean {
  return (BUILT_IN_PALETTE_IDS as readonly string[]).includes(paletteId);
}

/**
 * Title-cases palette labels for display (e.g. API `"my palette"` → `"My Palette"`).
 */
export function capitalizePaletteLabel(label: string): string {
  const t = label.trim();
  if (!t) return label;
  return t
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/** Default palette (`pal_jepto`). */
export const DEFAULT_PALETTE: PaletteConfig = BUILT_IN_PALETTES[0]!;

/**
 * Host apps load client palettes from `listItems.palettes`; built-ins stay first and
 * duplicates (same `paletteId` as a built-in) are dropped from the server slice.
 */
export function mergeBuiltInWithServerPalettes(
  server: PaletteConfig[]
): PaletteConfig[] {
  const builtInIds = new Set(BUILT_IN_PALETTES.map((p) => p.paletteId));
  const extra = server.filter((p) => p.paletteId && !builtInIds.has(p.paletteId));
  return [...BUILT_IN_PALETTES, ...extra];
}
